<template>
  <div class="modal-overlay" v-if="isVisible" @click.self="close">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">{{ $t('priceInquiryModal.title') }}</h4>
          <button type="button" class="btn-close" @click="close" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p>{{ $t('priceInquiryModal.description') }}</p>
          <template v-if="!isAuthenticated">
            <p>{{ $t('priceInquiryModal.description_auth') }}</p>
          </template>
          <template v-else>
            <p>{{ $t('priceInquiryModal.description_guest') }}</p>
          </template>

          <form @submit.prevent="submitInquiry">
            <div class="mb-3" :hidden="isAuthenticated">
              <label class="form-label">{{ $t('priceInquiryModal.name_label') }}</label>
              <input type="text" v-model="name" :placeholder="$t('priceInquiryModal.name_placeholder')" class="form-control"
                required  />
            </div>

            <div class="mb-3" :hidden="isAuthenticated">
              <label class="form-label">{{ $t('priceInquiryModal.phone_label') }}</label>
              <input type="number" v-model="phone" :placeholder="$t('priceInquiryModal.phone_placeholder')" class="form-control"
                required  />
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-primary" @click="submitInquiry">{{ $t('priceInquiryModal.submit') }}</button>
          <button type="button" class="btn btn-outline-primary" @click="close">{{ $t('priceInquiryModal.close') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const { t } = useI18n()
const toast = useToast()
const props = defineProps({
  product: { type: Object, required: true }
})
const emit = defineEmits(['close'])

const { authenticated } = useAuth()
const isVisible = ref(true)
const isAuthenticated = ref(authenticated?.value || false)
const name = ref('')
const phone = ref('')

onMounted(() => {
  if (isAuthenticated.value) {
    const auth = useAuth()
    name.value = auth.user?.value?.name || auth.user?.value?.username || ''
    phone.value = auth.user?.value?.mobile || ''
  }
})

const close = () => {
  isVisible.value = false
  emit('close')
}

const submitInquiry = async () => {
  try {
    const res = await $fetch('/market/api/products/inquiry', {
      method: 'POST',
      body: {
        ad_id: props.product.id,
        ad_user_id: props.product.userId,
        invoice_num: props.product.invoiceNum || '',
        call_phone: phone.value,
        family: name.value,
        ad_title: props.product.title
      }
    })
    if (res.success) {
      toast.success(t('priceInquiryModal.success'), { timeout: 4000 })
      close()
    } else {
      toast.add({
        title: `${res.message || t('priceInquiryModal.error')}`,
        description: '',
        color: 'red',
        icon: 'i-heroicons-exclamation-circle',
        timeout: 5000,
      })
      close()
    }
  } catch (e) {
    toast.add({
      title: `${e.data?.statusMessage || t('priceInquiryModal.error')}`,
      description: '',
      color: 'red',
      icon: 'i-heroicons-exclamation-circle',
      timeout: 5000,
    })
    close()
  }
}
</script>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.modal-dialog {
  width: 100%;
  max-width: 500px;
  margin: auto;
}

.modal-content {
  background: #fff;
  border-radius: $r-lg;
  overflow: hidden;
  box-shadow: $shadow-warm-lg;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid $line;

  .modal-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: $ink;
  }

  .btn-close {
    background: transparent;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.2s;
    &:hover { opacity: 1; }
  }
}

.modal-body {
  padding: 20px;
  font-size: 0.88rem;
  line-height: 1.8;
  color: $ink;

  p { margin: 0 0 14px; }

  .form-control {
    border-radius: $r-sm;
    border: 1px solid #ddd;
    padding: 10px 15px;
    font-family: inherit;
    width: 100%;

    &:focus {
      border-color: $leaf;
      box-shadow: 0 0 0 0.2rem rgba(3, 160, 84, 0.25);
      outline: none;
    }

    &[hidden] { display: none; }
  }

  .form-label {
    font-weight: 600;
    margin-bottom: 8px;
    display: block;
    font-size: 0.84rem;
  }

  .mb-3 { margin-bottom: 14px; }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 20px;
  border-top: 1px solid $line;

  .btn {
    padding: 8px 18px;
    border-radius: $r-pill;
    font-size: 0.84rem;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    transition: all 0.25s;
    border: 1.5px solid;

    &.btn-primary {
      background: $leaf;
      border-color: $leaf;
      color: #fff;

      &:hover { background: $leaf-deep; border-color: $leaf-deep; }
      &:active { transform: scale(0.97); }
    }

    &.btn-outline-primary {
      background: transparent;
      border-color: $leaf;
      color: $leaf;

      &:hover { background: $leaf; color: #fff; }
      &:active { transform: scale(0.97); }
    }
  }
}
</style>
